<script lang="ts">
import { z } from 'zod';
import validator from 'validator';
import TextField from '@/components/v2/TextField.vue';
import Checkbox from '@/components/v2/Checkbox.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Spinner from '@/components/v2/Spinner.vue';

export default {
  name: 'ManageProfile',
  components: { TextField, Checkbox, AppButton, Spinner },
};
</script>

<script setup lang="ts">
import { inject, computed, ref, onBeforeMount } from 'vue';
import { Store } from 'vuex';
import { AppState, Commit } from '@/store';
import { userProfileService } from '@/services/api';
import { UserProfile, UserProfilePatchRequest } from '@/types/User';
import { StoreMutationType } from '@/types/StoreMutationType';
import { useUser } from '@/composables/useUser';
import { UserRole } from '@/types/UserRole';

const profileFormSchema = z.object({
  name: z.string(),
  email: z.string(),
  phone: z.optional(
    z
      .string()
      .refine((val) => validator.isMobilePhone(val, 'en-US'))
      .or(z.literal('')),
  ),
  mfaEnabled: z.boolean(),
});

type ProfileFormSchemaType = z.infer<typeof profileFormSchema>;

const store = inject<Store<AppState>>('store');
const user = useUser();

// ---------------------
// data
// ---------------------
const profileFormRef = ref<HTMLFormElement | null>(null);
const userProfile = ref<UserProfile | undefined>(undefined);
const loading = ref(false);
const saving = ref(false);
const form = ref<ProfileFormSchemaType>({
  name: '',
  email: '',
  phone: '',
  mfaEnabled: false,
});

// Check if current user is admin
const isAdmin = computed(() => user.role.value === UserRole.Admin);

// ------------
// lifecycles
// ------------
onBeforeMount(async () => {
  await refresh();
});

// ---------------------
// computed properties
// ---------------------
const email = computed(() => {
  const email = store?.state.user?.email;
  return (email && email.trim()) || '';
});

const name = computed(() => {
  const name = store?.state.user?.name;
  return (name && name.trim()) || '';
});

const mfaEnabledErrors = computed(() => {
  const errors: string[] = [];
  if ((!profileFormSchema.shape.phone.safeParse(form.value.phone).success || !form.value.phone) && form.value.mfaEnabled) {
    errors.push('A valid phone number must be provided to enable mfa');
  }
  // Admin users cannot disable MFA
  if (isAdmin.value && userProfile.value?.mfaEnabled && !form.value.mfaEnabled) {
    errors.push('Admin users cannot disable MFA');
  }
  return errors;
});

const phoneErrors = computed(() => {
  const errors: string[] = [];
  if (!profileFormSchema.shape.phone.safeParse(form.value.phone).success) {
    errors.push('Please enter a valid phone number');
  }
  return errors;
});

const canSaveForm = computed(() => {
  return (
    mfaEnabledErrors.value.length === 0 &&
    phoneErrors.value.length === 0 &&
    (form.value.phone !== userProfile.value?.phone || form.value.mfaEnabled !== userProfile.value?.mfaEnabled)
  );
});

// ---------------------
// methods
// ---------------------
async function refresh(): Promise<void> {
  userProfile.value = await userProfileService.getUserProfile();

  form.value.name = name.value;
  form.value.email = email.value;
  form.value.phone = userProfile.value.phone ? userProfile.value.phone.substring(2) : '';
  form.value.mfaEnabled = userProfile.value.mfaEnabled;
}

async function submit(): Promise<void> {
  saving.value = true;

  if (!canSaveForm.value) {
    saving.value = false;
    return;
  }

  let validatedForm;

  try {
    validatedForm = profileFormSchema.parse(form.value);

    const request: UserProfilePatchRequest = {
      phone: validatedForm.phone ? '+1' + validatedForm.phone : undefined,
      mfaEnabled: validatedForm.mfaEnabled,
    };

    await userProfileService.updateUserProfile(request);
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Profile saved successfully!' });

    await refresh();
  } catch (e) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Error: Could not save profile', color: 'error' });
    return;
  } finally {
    saving.value = false;
  }
}

// -----------------------
// form validation rules
// -----------------------
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const keys = profileFormSchema.keyof();
const rules = new Map<z.infer<typeof keys>, Array<(field: string | number | boolean) => boolean | string>>();

rules.set('phone', [
  (field) => {
    return profileFormSchema.shape.phone.safeParse(field as string).success || 'Please enter a valid phone number';
  },
]);

rules.set('mfaEnabled', [
  (field) => {
    return !((!profileFormSchema.shape.phone.safeParse(form.value.phone).success || !form.value.phone) && field);
  },
]);
</script>

<template>
  <div class="tw:m-8 tw:mx-auto tw:w-[900px]">
    <div
      v-if="loading"
      class="tw:grid tw:h-[50vh] tw:content-center tw:items-center tw:justify-center tw:justify-items-center tw:text-center">
      <Spinner size="lg" />
    </div>

    <div
      v-if="!loading"
      class="tw:mx-auto tw:mb-16 tw:max-w-[450px]">
      <h1>Manage Profile</h1>

      <form
        @submit.prevent="submit"
        ref="profileFormRef"
        class="tw:mt-10 tw:flex tw:flex-col tw:gap-10">
        <fieldset class="tw:grid tw:grid-cols-1 tw:gap-4">
          <div>
            <TextField
              v-model="form.name"
              label="Name"
              disabled
              data-test="name" />
          </div>
        </fieldset>

        <fieldset class="tw:grid tw:grid-cols-1 tw:gap-4">
          <div>
            <TextField
              v-model="form.email"
              label="Email"
              disabled
              data-test="email" />
          </div>
        </fieldset>

        <fieldset class="tw:grid tw:grid-cols-1 tw:gap-4">
          <div class="tw:flex tw:flex-col tw:gap-2">
            <TextField
              v-model="form.phone"
              label="Phone #"
              placeholder="+1"
              data-test="phone"
              :error="phoneErrors.length > 0"
              :errorMessage="phoneErrors[0]" />
          </div>
        </fieldset>
        <fieldset class="tw:grid tw:grid-cols-1 tw:gap-4">
          <div class="tw:flex tw:flex-col tw:gap-2">
            <Checkbox
              v-model="form.mfaEnabled"
              label="Enable multi-factor authentication"
              :disabled="isAdmin && userProfile?.mfaEnabled" />
            <p
              v-if="isAdmin && userProfile?.mfaEnabled"
              class="tw:text-sm tw:text-gray-500">
              MFA is required for admin users
            </p>
            <p
              v-if="mfaEnabledErrors.length > 0"
              class="tw:text-sm tw:text-error">
              {{ mfaEnabledErrors[0] }}
            </p>
          </div>
        </fieldset>

        <div class="tw:text-right">
          <AppButton
            primary
            :disabled="saving || !canSaveForm"
            :loading="saving"
            data-test="save"
            @click="submit">
            Save
          </AppButton>
        </div>
      </form>
    </div>
  </div>
</template>
